import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideBell,
  lucideCheck,
  lucideCheckCheck,
  lucideCircleCheck,
  lucideDownload,
  lucideInbox,
  lucideLoaderCircle,
  lucideMailOpen,
  lucideShieldCheck,
  lucideTriangleAlert,
  lucideUserPlus,
  lucideX,
} from '@ng-icons/lucide';
import { BrnSheetImports } from '@spartan-ng/brain/sheet';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { NotificationStore } from '@shared/stores/notification.store';
import type {
  AppNotification,
  DownloadNotification,
  MessageNotification,
  ProgressNotification,
} from '@shared/models/notification.model';

/** Icon and tint per message category; anything unknown reads as neutral info. */
const CATEGORY_LOOK: Record<string, { icon: string; tone: string }> = {
  plugin_enquiry: { icon: 'lucideUserPlus', tone: 'bg-primary/10 text-primary' },
  customer_interest: { icon: 'lucideUserPlus', tone: 'bg-primary/10 text-primary' },
  analysis_completed: { icon: 'lucideCircleCheck', tone: 'bg-success-soft text-success' },
  campaign_completed: { icon: 'lucideCircleCheck', tone: 'bg-success-soft text-success' },
  execution_failed: { icon: 'lucideTriangleAlert', tone: 'bg-danger-soft text-danger' },
  role_changed: { icon: 'lucideShieldCheck', tone: 'bg-secondary text-foreground' },
  recipient_added: { icon: 'lucideInbox', tone: 'bg-secondary text-foreground' },
};
const NEUTRAL_LOOK = { icon: 'lucideBell', tone: 'bg-secondary text-muted-foreground' };

/**
 * The bell's panel: one place for what needs attention. "Gerade aktiv" pins
 * running jobs (with real progress), downloads and just-finished jobs on top;
 * below, leads, outcomes and account changes as one feed grouped by day.
 */
@Component({
  selector: 'app-notification-sheet',
  standalone: true,
  imports: [
    RouterLink,
    NgIconComponent,
    ...BrnSheetImports,
    ...HlmSheetImports,
    HlmButtonDirective,
  ],
  providers: [
    provideIcons({
      lucideBell,
      lucideCheck,
      lucideCheckCheck,
      lucideCircleCheck,
      lucideDownload,
      lucideInbox,
      lucideLoaderCircle,
      lucideMailOpen,
      lucideShieldCheck,
      lucideTriangleAlert,
      lucideUserPlus,
      lucideX,
    }),
  ],
  templateUrl: './notification-sheet.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationSheetComponent {
  readonly store = inject(NotificationStore);
  readonly filters = [
    { unread: false, label: 'Alle' },
    { unread: true, label: 'Ungelesen' },
  ] as const;

  readonly days = computed(() => {
    const groups: { label: string; items: MessageNotification[] }[] = [];
    for (const item of this.store.inbox.items()) {
      const label = this.dayLabel(item.timestamp);
      const group = groups.at(-1);
      if (group?.label === label) group.items.push(item);
      else groups.push({ label, items: [item] });
    }
    return groups;
  });

  look(item: MessageNotification) {
    return CATEGORY_LOOK[item.category ?? ''] ?? NEUTRAL_LOOK;
  }

  open(item: MessageNotification): void {
    if (!item.read) void this.store.inbox.markRead(item.id);
    this.store.closeSheet();
  }

  triggerDownload(notification: DownloadNotification): void {
    if (!notification.blob) return;
    const url = URL.createObjectURL(notification.blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = notification.filename;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    this.store.markRead(notification.id);
  }

  asDownload(n: AppNotification): DownloadNotification {
    return n as DownloadNotification;
  }
  asProgress(n: AppNotification): ProgressNotification {
    return n as ProgressNotification;
  }

  linkPath(link?: string): string {
    return link?.split('?')[0] ?? '';
  }
  linkQuery(link?: string): Record<string, string> {
    return Object.fromEntries(new URLSearchParams(link?.split('?')[1] ?? ''));
  }

  formatTime(date: Date): string {
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return 'Gerade eben';
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
    if (diff < 86400 && this.dayLabel(date) === 'Heute') return `vor ${Math.floor(diff / 3600)} Std.`;
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  private dayLabel(date: Date): string {
    const day = new Date(date).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    if (day === today) return 'Heute';
    if (day === today - 86_400_000) return 'Gestern';
    return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' });
  }
}
